import { v } from 'convex/values'

import type { Doc } from '../_generated/dataModel'
import type { QueryCtx } from '../_generated/server'
import { dayAmount, formatAmount } from './habitMeasure'
import { measureOn } from './levelUp'

// Intensity shading: a number habit's dots are filled by how much was logged
// against the day's target, not just whether the day was done. The credit
// matches habit strength's (lib/habitStrength.measureCredit): amount / target,
// capped at 1.

export const INTENSITY_MIN = 0.18
const GRID_LOG_LIMIT = 6000

export type ShadeMeasure = { target: number; direction: 'atLeast' | 'atMost' }

export type DayShade = {
  // 0 = nothing logged; otherwise the share of the target (0–1].
  credit: number
  // Beat the target (at-least habits only): drawn with a bright ring.
  over: boolean
}

/**
 * How full a day's cell is. Staying under an at-most cap counts as full; going
 * over it loses credit like habit strength does. 0 / not logged = no fill.
 */
export function dayShade(m: ShadeMeasure, amount: number): DayShade {
  if (!(amount > 0) || !(m.target > 0)) return { credit: 0, over: false }
  if (m.direction === 'atMost')
    return { credit: Math.min(1, m.target / Math.max(amount, m.target)), over: false }
  return { credit: Math.min(1, amount / m.target), over: amount > m.target }
}

/** Fill opacity for a credit: INTENSITY_MIN when anything was logged, 1 at target. */
export const shadeAlpha = (credit: number) =>
  credit <= 0 ? 0 : Math.min(1, INTENSITY_MIN + credit * (1 - INTENSITY_MIN))

/** Heat bucket 1–5 for the legend and for coarse renderers (0 = none). */
export const shadeLevel = (credit: number) => (credit <= 0 ? 0 : Math.min(5, Math.ceil(credit * 5)))

/** Whether a habit's dots shade by amount: number habits unless switched off. */
export const shadesByAmount = (h: Pick<Doc<'nodes'>, 'measure' | 'dotShade'>) =>
  !!h.measure && h.dotShade !== false

export const dayAmountValidator = v.object({
  date: v.string(),
  amount: v.number(),
  target: v.number(),
})
export type DayAmount = { date: string; amount: number; target: number }

/**
 * A number habit's logged amount per day since `fromKey` (with the target in
 * force that day), from the logs the views already read for strength.
 */
export async function loadDayAmounts(
  ctx: QueryCtx,
  habit: Doc<'nodes'>,
  fromKey: string,
): Promise<DayAmount[]> {
  const m = habit.measure
  if (!m) return []
  const logs = await ctx.db
    .query('habitLogs')
    .withIndex('by_habit_and_date', (q) => q.eq('habitId', habit._id).gte('date', fromKey))
    .take(GRID_LOG_LIMIT)
  const byDay = new Map<string, number[]>()
  for (const l of logs) byDay.set(l.date, [...(byDay.get(l.date) ?? []), l.amount])
  return [...byDay]
    .map(([date, amounts]) => ({
      date,
      amount: Math.round(dayAmount(amounts, m.aggregate) * 100) / 100,
      target: measureOn(m, habit.levelUp, date).target,
    }))
    .sort((a, b) => (a.date < b.date ? -1 : 1))
}

// ─── Client helpers shared by web and native ─────────────────────────────────

export type ShadeDay = DayShade & { amount: number; target: number }
export type ShadeMap = Map<string, ShadeDay>
export type ShadeUnit = { unit: string; direction: 'atLeast' | 'atMost' }
type T = (k: string, o?: Record<string, unknown>) => string

export function buildShadeMap(
  days: readonly DayAmount[] | undefined,
  direction: ShadeUnit['direction'],
): ShadeMap {
  const map: ShadeMap = new Map()
  for (const d of days ?? [])
    map.set(d.date, { ...dayShade({ target: d.target, direction }, d.amount), ...d })
  return map
}

const dayName = (lang: string, ds: string) =>
  new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(new Date(`${ds}T12:00:00`))

/** "Thu · 6 of 8 glasses" (or "… limit" for an at-most habit); null if nothing was logged. */
export function shadeTip(t: T, lang: string, ds: string, s: ShadeDay | undefined, m: ShadeUnit) {
  if (!s || s.amount <= 0) return null
  return t(m.direction === 'atMost' ? 'dotShade.tipLimit' : 'dotShade.tip', {
    day: dayName(lang, ds),
    amount: formatAmount(s.amount, lang),
    target: formatAmount(s.target, lang),
    unit: m.unit,
  }).trim()
}

/** "Avg 6.9 glasses · 5 days at target" over the last `days` days with an entry. */
export function shadeSummary(
  t: T,
  lang: string,
  shades: ShadeMap,
  m: ShadeUnit,
  today: string,
  days = 30,
) {
  const from = new Date(Date.parse(`${today}T00:00:00Z`) - (days - 1) * 86_400_000)
    .toISOString()
    .slice(0, 10)
  let sum = 0
  let n = 0
  let hit = 0
  for (const [ds, s] of shades) {
    if (ds < from || ds > today || s.amount <= 0) continue
    sum += s.amount
    n++
    if (s.credit >= 1) hit++
  }
  if (!n) return null
  return t(m.direction === 'atMost' ? 'dotShade.summaryLimit' : 'dotShade.summary', {
    avg: formatAmount(sum / n, lang),
    unit: m.unit,
    count: hit,
  }).trim()
}
